import { useEffect, useRef, useState } from 'react'

import { label } from '../../../lib/format'
import type { Flag, Validation } from '../../../lib/types'

interface Props {
  path: string
  name: string
  value: string | null
  confidence?: number
  issues: Validation[]
  edited: boolean
  mono?: boolean
  onCommit: (path: string, value: string | null) => void
  onFocusField?: (path: string) => void
}

function flagOf(issues: Validation[], confidence?: number): Flag {
  if (issues.some((i) => i.severity === 'error')) return 'error'
  if (issues.some((i) => i.severity === 'warn')) return 'warn'
  if (confidence !== undefined && confidence < 0.8) return 'warn'
  return 'ok'
}

export default function FieldInput({
  path, name, value, confidence, issues, edited, mono, onCommit, onFocusField,
}: Props) {
  const [local, setLocal] = useState(value ?? '')
  const dirty = useRef(false)

  // Accept upstream changes (a re-extract, or another field's fix cascading)
  // but never clobber what the user is actively typing.
  useEffect(() => {
    if (!dirty.current) setLocal(value ?? '')
  }, [value])

  const flag = flagOf(issues, confidence)
  const primary = issues.find((i) => i.severity === 'error') ?? issues[0]

  const commit = () => {
    dirty.current = false
    const next = local.trim() === '' ? null : local
    if (next !== value) onCommit(path, next)
  }

  return (
    <>
      <label className="field-label" htmlFor={`f-${path}`}>{label(name)}</label>
      <div className="field-control">
        <div className="field-input-row">
          <span
            className={`dot dot-${flag}`}
            title={
              confidence !== undefined
                ? `Confidence ${(confidence * 100).toFixed(0)}%`
                : undefined
            }
          />
          <input
            id={`f-${path}`}
            className={[
              'field-input',
              flag !== 'ok' ? `flag-${flag}` : '',
              edited ? 'edited' : '',
              mono ? 'mono' : '',
            ].filter(Boolean).join(' ')}
            value={local}
            onChange={(e) => { dirty.current = true; setLocal(e.target.value) }}
            onBlur={commit}
            onFocus={() => onFocusField?.(path)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.currentTarget.blur() }
              if (e.key === 'Escape') { dirty.current = false; setLocal(value ?? '') }
            }}
            placeholder="—"
            aria-invalid={flag === 'error'}
            aria-describedby={primary ? `n-${path}` : undefined}
          />
        </div>

        {primary && (
          <div
            id={`n-${path}`}
            className={`field-note ${primary.severity === 'error' ? 'error' : 'warn'}`}
          >
            <span>{primary.message}</span>
            {primary.suggestion && primary.suggestion !== local && (
              <button
                type="button"
                className="fix-btn"
                title={`Use "${primary.suggestion}"`}
                onClick={() => {
                  dirty.current = false
                  setLocal(primary.suggestion!)
                  onCommit(path, primary.suggestion!)
                }}
              >
                Use {primary.suggestion.length > 28
                  ? `${primary.suggestion.slice(0, 28)}…`
                  : primary.suggestion}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  )
}
